import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { FileText, Plug, Play, CheckCircle, Loader2, MapPin, Tag, Calendar, ChevronRight, Plus, GripVertical, AlertTriangle, ArrowRight } from 'lucide-react';
// Mock Data
const DETECTED_LOCATIONS = [{
  id: 1,
  name: 'Collina NBG Milutina Milankovića',
  orders: '~120 orders/day'
}, {
  id: 2,
  name: 'Collina NBG Gandijeva',
  orders: '~110 orders/day'
}, {
  id: 3,
  name: 'Collina Zemun',
  orders: '~95 orders/day'
}, {
  id: 4,
  name: 'Collina Vračar',
  orders: '~140 orders/day'
}, {
  id: 5,
  name: 'Collina Banovo Brdo',
  orders: '~85 orders/day'
}];
const CATEGORIES = [{
  name: 'Burgers',
  items: 124,
  price: '890 RSD',
  margin: 75,
  icon: '🍔'
}, {
  name: 'Pancakes',
  items: 87,
  price: '650 RSD',
  margin: 70,
  icon: '🥞'
}, {
  name: 'Sides',
  items: 45,
  price: '320 RSD',
  margin: 80,
  icon: '🍟'
}, {
  name: 'Drinks',
  items: 52,
  price: '250 RSD',
  margin: 65,
  icon: '🥤'
}, {
  name: 'Giros',
  items: 28,
  price: '750 RSD',
  margin: 68,
  icon: '🌯'
}, {
  name: 'Combos',
  items: 38,
  price: '1,150 RSD',
  margin: 72,
  icon: '🎁'
}];
interface WizardProps {
  onComplete: () => void;
}
export function Wizard({
  onComplete
}: WizardProps) {
  const [step, setStep] = useState(1);
  const [loadingStep, setLoadingStep] = useState(0);
  const [ungroupedLocations, setUngroupedLocations] = useState(DETECTED_LOCATIONS);
  const [groups, setGroups] = useState<{
    name: string;
    locations: typeof DETECTED_LOCATIONS;
  }[]>([{
    name: 'Novi Beograd',
    locations: []
  }]);
  const [margins, setMargins] = useState(CATEGORIES);
  // Auto-advance loading step
  useEffect(() => {
    if (step === 2) {
      const timers = [setTimeout(() => setLoadingStep(1), 800), setTimeout(() => setLoadingStep(2), 1600), setTimeout(() => setLoadingStep(3), 2400), setTimeout(() => setLoadingStep(4), 3200), setTimeout(() => {
        setLoadingStep(5);
        setTimeout(() => setStep(3), 1000);
      }, 4000)];
      return () => timers.forEach(clearTimeout);
    }
  }, [step]);
  // Drag and Drop Handlers
  const handleDragStart = (e: React.DragEvent, locationId: number, source: 'ungrouped' | string) => {
    e.dataTransfer.setData('locationId', locationId.toString());
    e.dataTransfer.setData('source', source);
  };
  const handleDrop = (e: React.DragEvent, targetGroup: string | 'ungrouped') => {
    e.preventDefault();
    const locationId = parseInt(e.dataTransfer.getData('locationId'));
    const source = e.dataTransfer.getData('source');
    if (source === targetGroup) return;
    let location;
    // Remove from source
    if (source === 'ungrouped') {
      location = ungroupedLocations.find(l => l.id === locationId);
      setUngroupedLocations(prev => prev.filter(l => l.id !== locationId));
    } else {
      const sourceGroup = groups.find(g => g.name === source);
      location = sourceGroup?.locations.find(l => l.id === locationId);
      setGroups(prev => prev.map(g => {
        if (g.name === source) {
          return {
            ...g,
            locations: g.locations.filter(l => l.id !== locationId)
          };
        }
        return g;
      }));
    }
    if (!location) return;
    // Add to target
    if (targetGroup === 'ungrouped') {
      setUngroupedLocations(prev => [...prev, location!]);
    } else {
      setGroups(prev => prev.map(g => {
        if (g.name === targetGroup) {
          return {
            ...g,
            locations: [...g.locations, location!]
          };
        }
        return g;
      }));
    }
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  const renderProgress = () => <div className="flex justify-center gap-2 mb-8" data-id="element-3297">
      {[1, 2, 3, 4, 5, 6].map(s => <div key={s} className={`w-2 h-2 rounded-full transition-all duration-300 ${s === step ? 'bg-accent-primary scale-125' : s < step ? 'bg-accent-primary' : 'bg-white/20'}`} data-id="element-3298" />)}
    </div>;
  return <div className="max-w-4xl mx-auto py-12 px-4 animate-in fade-in duration-500" data-id="element-3299">
      {renderProgress()}

      {/* STEP 1: WELCOME */}
      {step === 1 && <div className="text-center space-y-8" data-id="element-3300">
          <div data-id="element-3301">
            <h1 className="text-3xl font-bold mb-2" data-id="element-3302">
              Let's set up your analytics
            </h1>
            <p className="text-text-secondary text-lg" data-id="element-3303">
              Connect your data and we'll do the rest
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3304">
            <Card className="p-8 flex flex-col items-center gap-4 cursor-pointer hover:border-accent-primary transition-colors group" onClick={() => setStep(2)} data-id="element-3305">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-accent-primary/20 transition-colors" data-id="element-3306">
                <FileText size={32} className="text-accent-primary" data-id="element-3307" />
              </div>
              <div className="text-center" data-id="element-3308">
                <h3 className="font-bold text-lg mb-1" data-id="element-3309">Upload CSV</h3>
                <p className="text-sm text-text-secondary" data-id="element-3310">
                  Drop your Wolt export file
                </p>
              </div>
            </Card>

            <Card className="p-8 flex flex-col items-center gap-4 cursor-pointer hover:border-accent-primary transition-colors group" onClick={() => setStep(2)} data-id="element-3311">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-accent-primary/20 transition-colors" data-id="element-3312">
                <Plug size={32} className="text-accent-primary" data-id="element-3313" />
              </div>
              <div className="text-center" data-id="element-3314">
                <h3 className="font-bold text-lg mb-1" data-id="element-3315">Connect API</h3>
                <p className="text-sm text-text-secondary" data-id="element-3316">
                  Link your Wolt platform
                </p>
              </div>
            </Card>

            <Card className="p-8 flex flex-col items-center gap-4 cursor-pointer hover:border-accent-primary transition-colors group" onClick={() => setStep(2)} data-id="element-3317">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-accent-primary/20 transition-colors" data-id="element-3318">
                <Play size={32} className="text-accent-primary" data-id="element-3319" />
              </div>
              <div className="text-center" data-id="element-3320">
                <h3 className="font-bold text-lg mb-1" data-id="element-3321">Try Demo</h3>
                <p className="text-sm text-text-secondary" data-id="element-3322">
                  Explore sample restaurant data
                </p>
              </div>
            </Card>
          </div>

          <div className="flex justify-between items-center text-sm text-text-secondary pt-8" data-id="element-3323">
            <span data-id="element-3324">Step 1 of 6</span>
            <button className="hover:text-white transition-colors" data-id="element-3325">
              Need help?
            </button>
          </div>
        </div>}

      {/* STEP 2: LOADING */}
      {step === 2 && <div className="text-center space-y-8 max-w-md mx-auto" data-id="element-3326">
          <h1 className="text-2xl font-bold" data-id="element-3327">Analyzing your data...</h1>

          <div className="flex justify-center py-8" data-id="element-3328">
            <Loader2 size={48} className="text-accent-primary animate-spin" data-id="element-3329" />
          </div>

          <div className="space-y-4 text-left bg-background-elevated p-6 rounded-xl border border-white/5" data-id="element-3330">
            <div className="flex items-center gap-3" data-id="element-3331">
              {loadingStep >= 1 ? <CheckCircle size={20} className="text-accent-success" data-id="element-3332" /> : <div className="w-5 h-5 rounded-full border-2 border-white/20" data-id="element-3333" />}
              <span className={loadingStep >= 1 ? 'text-white' : 'text-text-secondary'} data-id="element-3334">
                Found 5 locations
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-3335">
              {loadingStep >= 2 ? <CheckCircle size={20} className="text-accent-success" data-id="element-3336" /> : <div className="w-5 h-5 rounded-full border-2 border-white/20" data-id="element-3337" />}
              <span className={loadingStep >= 2 ? 'text-white' : 'text-text-secondary'} data-id="element-3338">
                Detected 847 unique items
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-3339">
              {loadingStep >= 3 ? <CheckCircle size={20} className="text-accent-success" data-id="element-3340" /> : <div className="w-5 h-5 rounded-full border-2 border-white/20" data-id="element-3341" />}
              <span className={loadingStep >= 3 ? 'text-white' : 'text-text-secondary'} data-id="element-3342">
                Identified 6 categories
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-3343">
              {loadingStep >= 4 ? <CheckCircle size={20} className="text-accent-success" data-id="element-3344" /> : loadingStep === 3 ? <Loader2 size={20} className="text-accent-primary animate-spin" data-id="element-3345" /> : <div className="w-5 h-5 rounded-full border-2 border-white/20" data-id="element-3346" />}
              <span className={loadingStep >= 4 ? 'text-white' : 'text-text-secondary'} data-id="element-3347">
                Calculating patterns...
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-3348">
              {loadingStep >= 5 ? <CheckCircle size={20} className="text-accent-success" data-id="element-3349" /> : loadingStep === 4 ? <Loader2 size={20} className="text-accent-primary animate-spin" data-id="element-3350" /> : <div className="w-5 h-5 rounded-full border-2 border-white/20" data-id="element-3351" />}
              <span className={loadingStep >= 5 ? 'text-white' : 'text-text-secondary'} data-id="element-3352">
                Preparing recommendations
              </span>
            </div>
          </div>

          <p className="text-sm text-text-secondary" data-id="element-3353">
            This usually takes about 10 seconds
          </p>
        </div>}

      {/* STEP 3: RESULTS */}
      {step === 3 && <div className="space-y-8" data-id="element-3354">
          <div className="text-center" data-id="element-3355">
            <h1 className="text-2xl font-bold mb-2 flex items-center justify-center gap-2" data-id="element-3356">
              <CheckCircle className="text-accent-success" data-id="element-3357" /> Here's what we
              found
            </h1>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3358">
            <Card className="p-4 flex items-center gap-3" data-id="element-3359">
              <MapPin className="text-accent-primary" data-id="element-3360" />
              <div data-id="element-3361">
                <div className="font-bold text-lg" data-id="element-3362">5</div>
                <div className="text-xs text-text-secondary" data-id="element-3363">Locations</div>
              </div>
            </Card>
            <Card className="p-4 flex items-center gap-3" data-id="element-3364">
              <Tag className="text-accent-primary" data-id="element-3365" />
              <div data-id="element-3366">
                <div className="font-bold text-lg" data-id="element-3367">847</div>
                <div className="text-xs text-text-secondary" data-id="element-3368">Items</div>
              </div>
            </Card>
            <Card className="p-4 flex items-center gap-3" data-id="element-3369">
              <FileText className="text-accent-primary" data-id="element-3370" />
              <div data-id="element-3371">
                <div className="font-bold text-lg" data-id="element-3372">6</div>
                <div className="text-xs text-text-secondary" data-id="element-3373">Categories</div>
              </div>
            </Card>
            <Card className="p-4 flex items-center gap-3" data-id="element-3374">
              <Calendar className="text-accent-primary" data-id="element-3375" />
              <div data-id="element-3376">
                <div className="font-bold text-lg" data-id="element-3377">3</div>
                <div className="text-xs text-text-secondary" data-id="element-3378">Months Data</div>
              </div>
            </Card>
          </div>

          <Card className="p-0 overflow-hidden" data-id="element-3379">
            <div className="p-4 border-b border-white/10 font-bold bg-white/5" data-id="element-3380">
              Detected Locations
            </div>
            <div className="divide-y divide-white/5" data-id="element-3381">
              {DETECTED_LOCATIONS.map(loc => <div key={loc.id} className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors" data-id="element-3382">
                  <div className="flex items-center gap-3" data-id="element-3383">
                    <input type="checkbox" defaultChecked className="rounded border-white/20 bg-transparent text-accent-primary focus:ring-accent-primary" data-id="element-3384" />
                    <span data-id="element-3385">{loc.name}</span>
                  </div>
                  <div className="flex gap-2" data-id="element-3386">
                    <Button size="sm" variant="secondary" data-id="element-3387">
                      Rename
                    </Button>
                    <Button size="sm" variant="secondary" className="text-accent-error hover:bg-accent-error/10 border-accent-error/20" data-id="element-3388">
                      Remove
                    </Button>
                  </div>
                </div>)}
            </div>
          </Card>

          <div className="flex justify-between items-center pt-4" data-id="element-3389">
            <button className="text-text-secondary hover:text-white text-sm" data-id="element-3390">
              Something's missing?
            </button>
            <Button onClick={() => setStep(4)} data-id="element-3391">Looks good</Button>
          </div>
        </div>}

      {/* STEP 4: GROUPING */}
      {step === 4 && <div className="space-y-8" data-id="element-3392">
          <div className="text-center" data-id="element-3393">
            <h1 className="text-2xl font-bold mb-2" data-id="element-3394">
              How should we group your locations?
            </h1>
            <p className="text-text-secondary" data-id="element-3395">
              Groups help you compare performance across regions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-3396">
            {/* Ungrouped Column */}
            <div className="bg-background-elevated/50 p-4 rounded-xl border-2 border-dashed border-white/10 min-h-[400px]" onDragOver={handleDragOver} onDrop={e => handleDrop(e, 'ungrouped')} data-id="element-3397">
              <h3 className="font-bold text-sm text-text-secondary uppercase mb-4" data-id="element-3398">
                Ungrouped
              </h3>
              <div className="space-y-3" data-id="element-3399">
                {ungroupedLocations.map(loc => <Card key={loc.id} className="p-3 cursor-grab active:cursor-grabbing hover:border-accent-primary transition-colors" draggable onDragStart={e => handleDragStart(e, loc.id, 'ungrouped')} data-id="element-3400">
                    <div className="flex items-center gap-2" data-id="element-3401">
                      <GripVertical size={16} className="text-text-secondary" data-id="element-3402" />
                      <div data-id="element-3403">
                        <div className="font-medium" data-id="element-3404">{loc.name}</div>
                        <div className="text-xs text-text-secondary" data-id="element-3405">
                          {loc.orders}
                        </div>
                      </div>
                    </div>
                  </Card>)}
              </div>
            </div>

            {/* Groups Column */}
            <div className="space-y-6" data-id="element-3406">
              {groups.map(group => <div key={group.name} className="bg-background-elevated p-4 rounded-xl border border-white/10 min-h-[150px]" onDragOver={handleDragOver} onDrop={e => handleDrop(e, group.name)} data-id="element-3407">
                  <h3 className="font-bold text-sm text-text-secondary uppercase mb-4" data-id="element-3408">
                    {group.name}
                  </h3>
                  <div className="space-y-3" data-id="element-3409">
                    {group.locations.map(loc => <Card key={loc.id} className="p-3 cursor-grab active:cursor-grabbing hover:border-accent-primary transition-colors" draggable onDragStart={e => handleDragStart(e, loc.id, group.name)} data-id="element-3410">
                        <div className="flex items-center gap-2" data-id="element-3411">
                          <GripVertical size={16} className="text-text-secondary" data-id="element-3412" />
                          <div data-id="element-3413">
                            <div className="font-medium" data-id="element-3414">{loc.name}</div>
                            <div className="text-xs text-text-secondary" data-id="element-3415">
                              {loc.orders}
                            </div>
                          </div>
                        </div>
                      </Card>)}
                    {group.locations.length === 0 && <div className="text-center py-4 text-sm text-text-secondary border-2 border-dashed border-white/5 rounded-lg" data-id="element-3416">
                        Drag locations here
                      </div>}
                  </div>
                </div>)}

              <Button variant="secondary" className="w-full border-dashed border-white/20 py-6" icon={<Plus size={16} data-id="element-3418" />} onClick={() => setGroups([...groups, {
            name: 'New Group',
            locations: []
          }])} data-id="element-3417">
                Create new group
              </Button>
            </div>
          </div>

          <div className="flex justify-between items-center pt-4" data-id="element-3419">
            <button className="text-text-secondary hover:text-white text-sm" onClick={() => setStep(5)} data-id="element-3420">
              Skip this step
            </button>
            <Button onClick={() => setStep(5)} data-id="element-3421">Continue</Button>
          </div>
        </div>}

      {/* STEP 5: MARGINS */}
      {step === 5 && <div className="space-y-8" data-id="element-3422">
          <div className="text-center" data-id="element-3423">
            <h1 className="text-2xl font-bold mb-2" data-id="element-3424">
              Set your margins by category
            </h1>
            <p className="text-text-secondary" data-id="element-3425">
              We'll use these to calculate your actual profit
            </p>
          </div>

          <Card className="overflow-hidden" data-id="element-3426">
            <table className="w-full text-sm" data-id="element-3427">
              <thead data-id="element-3428">
                <tr className="bg-white/5 border-b border-white/10 text-text-secondary" data-id="element-3429">
                  <th className="text-left p-4 font-medium" data-id="element-3430">CATEGORY</th>
                  <th className="text-left p-4 font-medium" data-id="element-3431">ITEMS</th>
                  <th className="text-left p-4 font-medium" data-id="element-3432">AVG PRICE</th>
                  <th className="text-left p-4 font-medium" data-id="element-3433">MARGIN</th>
                </tr>
              </thead>
              <tbody data-id="element-3434">
                {margins.map((cat, i) => <tr key={i} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors" data-id="element-3435">
                    <td className="p-4 font-medium flex items-center gap-2" data-id="element-3436">
                      <span data-id="element-3437">{cat.icon}</span> {cat.name}
                    </td>
                    <td className="p-4 text-text-secondary" data-id="element-3438">{cat.items}</td>
                    <td className="p-4 text-text-secondary" data-id="element-3439">{cat.price}</td>
                    <td className="p-4" data-id="element-3440">
                      <div className="relative w-24" data-id="element-3441">
                        <input type="number" defaultValue={cat.margin} className="w-full bg-background border border-white/20 rounded px-2 py-1 text-right pr-6 focus:outline-none focus:border-accent-primary" data-id="element-3442" />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-text-secondary" data-id="element-3443">
                          %
                        </span>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </Card>

          <div className="flex justify-between items-center pt-4" data-id="element-3444">
            <button className="text-text-secondary hover:text-white text-sm" data-id="element-3445">
              Edit categories
            </button>
            <span className="text-text-secondary text-sm hidden md:inline" data-id="element-3446">
              Don't worry, you can adjust these later
            </span>
            <Button onClick={() => setStep(6)} data-id="element-3447">Continue</Button>
          </div>
        </div>}

      {/* STEP 6: COMPLETE */}
      {step === 6 && <div className="text-center space-y-8 max-w-md mx-auto py-12" data-id="element-3448">
          <div className="w-20 h-20 bg-accent-success/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-in zoom-in duration-500" data-id="element-3449">
            <CheckCircle size={40} className="text-accent-success" data-id="element-3450" />
          </div>

          <div data-id="element-3451">
            <h1 className="text-3xl font-bold mb-2" data-id="element-3452">You're all set!</h1>
            <p className="text-text-secondary text-lg" data-id="element-3453">
              Your dashboard is ready
            </p>
          </div>

          <Card className="p-6 text-left space-y-4 bg-background-elevated border-accent-primary/20" data-id="element-3454">
            <div className="flex items-center gap-3" data-id="element-3455">
              <MapPin className="text-accent-primary" size={20} data-id="element-3456" />
              <span data-id="element-3457">
                Tracking <strong data-id="element-3458">5 locations</strong>
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-3459">
              <Tag className="text-accent-primary" size={20} data-id="element-3460" />
              <span data-id="element-3461">
                <strong data-id="element-3462">847 menu items</strong> categorized
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-3463">
              <Calendar className="text-accent-primary" size={20} data-id="element-3464" />
              <span data-id="element-3465">
                <strong data-id="element-3466">3 months</strong> of historical data loaded
              </span>
            </div>
          </Card>

          <div className="space-y-4 pt-4" data-id="element-3467">
            <Button className="w-full py-6 text-lg" onClick={onComplete} data-id="element-3468">
              Go to Dashboard <ArrowRight className="ml-2" data-id="element-3469" />
            </Button>
            <button className="text-text-secondary hover:text-white text-sm" data-id="element-3470">
              Adjust settings
            </button>
          </div>
        </div>}
    </div>;
}